import type { MessageId } from '@/shared/i18n/catalogue.en'
import {
  CAPABILITIES,
  hasCapability,
  type CapabilityName,
} from '@/shared/session/capabilities'
import type { Session } from '@/shared/session/types'
import type { IconName } from '@/shared/ui/icons'
import type { NavTone } from '@/shared/workspace/navPanel'
import type { QuickAccessId } from '@/shared/workspace/quickAccessIds'

/**
 * THE FINANCE AREA'S SECTIONS, NAMED ONCE — the same table the Leads area
 * keeps. The panel draws them, the router titles its routes from them and
 * `app/quickAccessCatalogue` reads its entries' faces from here, so a section
 * cannot wear one glyph in the navigation and another in the quick access.
 *
 * The tone is the area's, and the area is administrative work: recording
 * money and closing months is the desk's business, reached from an `admin`
 * tile, whatever section is open inside it.
 *
 * The catalogues the desk keeps — the services, the price lists, the taxes
 * and the third-party costs an estimate quotes — are sections here by
 * operator decision rather than tiles of their own: what the Clinic charges
 * and what it records as paid are one desk's work. Services stand before the
 * price lists on purpose: a clinic that only names what it does and what it
 * costs never has to meet a price list, which the default list answers for.
 */
export const FINANCE_AREA_TONE: NavTone = 'admin'

export const FINANCE_NAV_ITEMS = {
  accounts: {
    icon: 'credit-card',
    label: 'finance.nav.accounts',
    description: 'finance.nav.accountsDescription',
  },
  people: {
    icon: 'person',
    label: 'finance.nav.people',
    description: 'finance.nav.peopleDescription',
  },
  prepaid: {
    icon: 'credit-card',
    label: 'finance.prepaid.title',
    description: 'finance.prepaid.description',
  },
  services: {
    // What the Clinic does, named before what it charges for it.
    icon: 'stethoscope',
    label: 'finance.nav.services',
    description: 'finance.nav.servicesDescription',
  },
  'price-lists': {
    icon: 'tag',
    label: 'finance.nav.priceLists',
    description: 'finance.nav.priceListsDescription',
  },
  taxes: {
    icon: 'receipt',
    label: 'finance.nav.taxes',
    description: 'finance.nav.taxesDescription',
  },
  'external-costs': {
    // Money the clinic pays out, the other half of the price tag above it.
    icon: 'coins',
    label: 'finance.nav.externalCosts',
    description: 'finance.nav.externalCostsDescription',
  },
  'cash-registers': { icon: 'receipt', label: 'cashRegister.title', description: 'cashRegister.description' },
  administration: {
    icon: 'sliders',
    label: 'finance.nav.administration',
    description: 'finance.nav.administrationDescription',
  },
} as const satisfies Record<string, { icon: IconName; label: MessageId; description: MessageId }>

export type FinanceSection = keyof typeof FINANCE_NAV_ITEMS

/** The sections in the panel's order: declaration order, read once. */
export const FINANCE_SECTIONS = Object.keys(FINANCE_NAV_ITEMS) as readonly FinanceSection[]

/**
 * The address each section's route declares, as literals the router can
 * check, and the bookmark its tile pins. Accounts keeps the area's own
 * bookmark id (`finance`), the way `/leads` keeps `leads`, because it is the
 * section the area's door lands an account reader on.
 */
export type FinanceSectionPath = `/finance/${FinanceSection}`

export const FINANCE_SECTION_LANDING: Record<FinanceSection, { id: QuickAccessId; to: FinanceSectionPath }> = {
  'cash-registers': { id: 'finance-cash-registers', to: '/finance/cash-registers' },
  accounts: { id: 'finance', to: '/finance/accounts' },
  people: { id: 'finance-people', to: '/finance/people' },
  prepaid: { id: 'finance-prepaid', to: '/finance/prepaid' },
  services: { id: 'finance-services', to: '/finance/services' },
  'price-lists': { id: 'finance-price-lists', to: '/finance/price-lists' },
  taxes: { id: 'finance-taxes', to: '/finance/taxes' },
  'external-costs': { id: 'finance-external-costs', to: '/finance/external-costs' },
  administration: { id: 'finance-administration', to: '/finance/administration' },
}

/**
 * WHAT EACH SECTION'S OWN ROUTE RUNS. People is under the accounts read on
 * purpose: a financial Person exists to own accounts and remit onto them, and
 * the server gates every person route on `finance.accounts.read` too. Taxes
 * ride the price-list read the same way: the server answers the tax
 * catalogues to `finance.price_list.read`, since a tax is a term of a price.
 * Administration admits EITHER the profile or the period reader — the two are
 * distinct capabilities, and a period manager holding neither accounts nor
 * clinical grants still reaches their own section. Services ride the same
 * price-list read as well: the server answers the service registry and the
 * prices it carries to `finance.price_list.read` and takes a change to either
 * on `finance.price_list.manage`.
 */
export const FINANCE_SECTION_CAPABILITIES: Record<FinanceSection, readonly CapabilityName[]> = {
  'cash-registers': [CAPABILITIES.financeRegistersRead],
  accounts: [CAPABILITIES.financeAccountsRead],
  people: [CAPABILITIES.financeAccountsRead],
  prepaid: [CAPABILITIES.financePrepaidRead],
  services: [CAPABILITIES.financePriceListRead],
  'price-lists': [CAPABILITIES.financePriceListRead],
  taxes: [CAPABILITIES.financePriceListRead],
  'external-costs': [CAPABILITIES.financeExternalCostCatalogRead],
  administration: [CAPABILITIES.financeProfileRead, CAPABILITIES.financePeriodsRead],
}

export function financeSectionAdmitted(
  session: Session | undefined,
  section: FinanceSection,
): boolean {
  return FINANCE_SECTION_CAPABILITIES[section].some((capability) =>
    hasCapability(session, capability),
  )
}

/**
 * Where `/finance` lands THIS account, or `null` when no section admits it —
 * which is also the workspace tile's absence. Accounts is the primary
 * section; after it the sections stand in the panel's own order, so a reader
 * of prices alone lands on the services and a period manager on
 * Administration. The door in `shared/workspace/workspaceDestinations.ts`
 * reads this answer rather than restating it.
 */
export function defaultFinanceSection(session: Session | undefined): FinanceSection | null {
  return FINANCE_SECTIONS.find((section) => financeSectionAdmitted(session, section)) ?? null
}
